import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { BackOfficeStorageService } from "./storage.service";

export interface BackOfficeThemeOption {
  id: string;
  label: string;
  description: string;
  className: string;
  colors: {
    primary: string;
    secondary: string;
    sidebar: string;
  };
}

export interface BackOfficeFontOption {
  id: string;
  label: string;
  description: string;
  fontFamily: string;
}

const BACK_OFFICE_THEMES: BackOfficeThemeOption[] = [
  {
    id: "ocean",
    label: "Ocean",
    description: "Classic back-office blue theme.",
    className: "backOffice-theme-ocean",
    colors: {
      primary: "#186ba0",
      secondary: "#6c757d",
      sidebar: "#186ba0",
    },
  },
  {
    id: "emerald",
    label: "Emerald",
    description: "Fresh green theme for a calmer workspace.",
    className: "backOffice-theme-emerald",
    colors: {
      primary: "#167f5f",
      secondary: "#63746f",
      sidebar: "#0f6f55",
    },
  },
  {
    id: "graphite",
    label: "Graphite",
    description: "Neutral slate theme with a focused look.",
    className: "backOffice-theme-graphite",
    colors: {
      primary: "#315f7d",
      secondary: "#64748b",
      sidebar: "#1f3346",
    },
  },
  {
    id: "cranberry",
    label: "Cranberry",
    description: "Warm accent theme with deeper navigation.",
    className: "backOffice-theme-cranberry",
    colors: {
      primary: "#b63d5a",
      secondary: "#75626a",
      sidebar: "#7d2840",
    },
  },
  {
    id: "ruby",
    label: "Ruby",
    description: "Deep red theme for a bold back-office look.",
    className: "backOffice-theme-ruby",
    colors: {
      primary: "#c81e3a",
      secondary: "#716064",
      sidebar: "#8a1028",
    },
  },
  {
    id: "scarlet",
    label: "Scarlet",
    description: "Classic red theme with strong navigation contrast.",
    className: "backOffice-theme-scarlet",
    colors: {
      primary: "#b91c1c",
      secondary: "#746160",
      sidebar: "#7f1d1d",
    },
  },
  {
    id: "sapphire",
    label: "Sapphire",
    description: "Bright blue theme with strong navigation contrast.",
    className: "backOffice-theme-sapphire",
    colors: {
      primary: "#2563eb",
      secondary: "#64748b",
      sidebar: "#1e3a8a",
    },
  },
  {
    id: "violet",
    label: "Violet",
    description: "Polished purple theme with a crisp workspace feel.",
    className: "backOffice-theme-violet",
    colors: {
      primary: "#7c3aed",
      secondary: "#6b6478",
      sidebar: "#4c1d95",
    },
  },
  {
    id: "aurora",
    label: "Aurora",
    description: "Cool cyan theme for a clean operational look.",
    className: "backOffice-theme-aurora",
    colors: {
      primary: "#0e879f",
      secondary: "#5f747a",
      sidebar: "#155e75",
    },
  },
  {
    id: "sunrise",
    label: "Sunrise",
    description: "Warm amber theme with clear active states.",
    className: "backOffice-theme-sunrise",
    colors: {
      primary: "#d97706",
      secondary: "#74685a",
      sidebar: "#92400e",
    },
  },
  {
    id: "olive",
    label: "Olive",
    description: "Grounded green theme with a softer contrast.",
    className: "backOffice-theme-olive",
    colors: {
      primary: "#607d20",
      secondary: "#68705d",
      sidebar: "#3f6212",
    },
  },
  {
    id: "onyx",
    label: "Onyx",
    description: "Sharp black theme with a clean neutral surface.",
    className: "backOffice-theme-onyx",
    colors: {
      primary: "#111827",
      secondary: "#6b7280",
      sidebar: "#020617",
    },
  },
  {
    id: "charcoal",
    label: "Charcoal",
    description: "Soft black theme for a focused operational UI.",
    className: "backOffice-theme-charcoal",
    colors: {
      primary: "#374151",
      secondary: "#6b7280",
      sidebar: "#1f2937",
    },
  },
  {
    id: "cloud",
    label: "Cloud",
    description: "Airy blue surfaces with calm, clear navigation.",
    className: "backOffice-theme-cloud",
    colors: {
      primary: "#3977a8",
      secondary: "#6f8191",
      sidebar: "#4b7394",
    },
  },
  {
    id: "sage",
    label: "Sage",
    description: "Soft green surfaces for a balanced workspace.",
    className: "backOffice-theme-sage",
    colors: {
      primary: "#3f7f70",
      secondary: "#71817b",
      sidebar: "#47766c",
    },
  },
  {
    id: "blush",
    label: "Blush",
    description: "Light rose surfaces with understated accents.",
    className: "backOffice-theme-blush",
    colors: {
      primary: "#9a6072",
      secondary: "#89777d",
      sidebar: "#825d69",
    },
  },
];

const BACK_OFFICE_FONT_OPTIONS: BackOfficeFontOption[] = [
  {
    id: "sans-serif",
    label: "Sans Serif",
    description: "Clean default font for everyday back-office work.",
    fontFamily: '"Segoe UI", Arial, sans-serif',
  },
  {
    id: "system",
    label: "System",
    description: "Uses the operating system interface font.",
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  {
    id: "aptos",
    label: "Aptos",
    description: "Modern Microsoft font with a polished business feel.",
    fontFamily: 'Aptos, Calibri, "Segoe UI", sans-serif',
  },
  {
    id: "humanist",
    label: "Humanist",
    description: "Warm, highly readable font for data-heavy screens.",
    fontFamily: 'Calibri, Candara, "Segoe UI", sans-serif',
  },
  {
    id: "helvetica",
    label: "Helvetica",
    description: "Crisp neutral font suited to professional interfaces.",
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  {
    id: "verdana",
    label: "Verdana",
    description: "Open, readable font for dense tables and forms.",
    fontFamily: 'Verdana, Geneva, sans-serif',
  },
  {
    id: "serif",
    label: "Serif",
    description: "A more formal reading style for detail-heavy screens.",
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  {
    id: "monospace",
    label: "Monospace",
    description: "Fixed-width font for scanning ids, codes, and logs.",
    fontFamily: 'Consolas, "Liberation Mono", "Courier New", monospace',
  },
  {
    id: "rounded",
    label: "Rounded",
    description: "Softer interface font with a friendly feel.",
    fontFamily: '"Trebuchet MS", "Segoe UI", Arial, sans-serif',
  },
];

@Injectable({
  providedIn: "root",
})
export class BackOfficeThemeService {
  private readonly themeStorageKey = "appearance.theme";
  private readonly fontStorageKey = "appearance.font";
  private readonly activeThemeSubject = new BehaviorSubject<BackOfficeThemeOption>(
    BACK_OFFICE_THEMES[0],
  );
  private readonly activeFontSubject = new BehaviorSubject<BackOfficeFontOption>(
    BACK_OFFICE_FONT_OPTIONS[0],
  );

  readonly activeTheme$ = this.activeThemeSubject.asObservable();
  readonly activeFont$ = this.activeFontSubject.asObservable();
  readonly themeOptions = BACK_OFFICE_THEMES;
  readonly fontOptions = BACK_OFFICE_FONT_OPTIONS;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private storageService: BackOfficeStorageService,
  ) {
    const storedThemeId = this.storageService.get<string>(this.themeStorageKey);
    const storedFontId = this.storageService.get<string>(this.fontStorageKey);
    const theme = this.getThemeById(storedThemeId);
    const font = this.getFontById(storedFontId);

    this.activeThemeSubject.next(theme);
    this.activeFontSubject.next(font);
    this.applyAppearance(theme, font);
  }

  get activeTheme(): BackOfficeThemeOption {
    return this.activeThemeSubject.value;
  }

  get activeFont(): BackOfficeFontOption {
    return this.activeFontSubject.value;
  }

  setTheme(themeId: string): void {
    const theme = this.getThemeById(themeId);

    this.storageService.set(this.themeStorageKey, theme.id);
    this.activeThemeSubject.next(theme);
    this.applyAppearance(theme, this.activeFont);
  }

  setFont(fontId: string): void {
    const font = this.getFontById(fontId);

    this.storageService.set(this.fontStorageKey, font.id);
    this.activeFontSubject.next(font);
    this.applyAppearance(this.activeTheme, font);
  }

  getThemeById(themeId: string | null | undefined): BackOfficeThemeOption {
    return (
      this.themeOptions.find((theme) => theme.id === themeId) ||
      this.themeOptions[0]
    );
  }

  getFontById(fontId: string | null | undefined): BackOfficeFontOption {
    return (
      this.fontOptions.find((font) => font.id === fontId) ||
      this.fontOptions[0]
    );
  }

  private applyAppearance(
    theme: BackOfficeThemeOption,
    font: BackOfficeFontOption,
  ): void {
    if (!this.document.body) {
      return;
    }

    this.themeOptions.forEach((themeOption) =>
      this.document.body.classList.remove(themeOption.className),
    );

    this.document.body.classList.add(theme.className);
    this.document.body.style.setProperty(
      "--backOffice-font-family",
      font.fontFamily,
    );
  }
}
